/**
 * Category level color configuration
 * Defines colors for each hierarchy level (0 = root, 1 = first sublevel, etc.)
 */

export const CATEGORY_LEVEL_COLORS: Record<number, string> = {
  0: "#1e40af", // Blue 700 - Root level
  1: "#7c3aed", // Violet 600 - First sublevel
  2: "#059669", // Emerald 600 - Second sublevel
  3: "#dc2626", // Red 600 - Third sublevel
  4: "#ea580c", // Orange 600 - Fourth sublevel
  5: "#0891b2", // Cyan 600 - Fifth sublevel
};

/**
 * Get the color for a category based on its level
 * Falls back to a default color if level is beyond defined colors
 * @param level - Category hierarchy level (0 = root)
 * @returns Hex color code
 */
export const getCategoryLevelColor = (level: number): string => {
  // If level is defined in the map, use it
  if (level in CATEGORY_LEVEL_COLORS) {
    return CATEGORY_LEVEL_COLORS[level];
  }

  // For levels beyond defined colors, cycle through the colors
  const colorKeys = Object.keys(CATEGORY_LEVEL_COLORS).map(Number);
  const maxLevel = Math.max(...colorKeys);
  const cycledLevel = level % (maxLevel + 1);

  return CATEGORY_LEVEL_COLORS[cycledLevel] || "#6b7280"; // Fallback to gray
};

/**
 * Get all defined level colors for UI display
 * @returns Array of {level, color} objects
 */
export const getAllLevelColors = (): Array<{
  level: number;
  color: string;
}> => {
  return Object.entries(CATEGORY_LEVEL_COLORS).map(([level, color]) => ({
    level: Number(level),
    color,
  }));
};
